import Phaser from "phaser";
import type { Cell } from "../entities/GridPath";
import { TOWER_LIST, TOWERS } from "../data/towers";
import type { Element, TowerDef, TowerId } from "../types";
import { ELEMENT_META } from "../types";
import { makeCaster } from "../entities/CasterSprite";
import { AD_REWARD_GOLD } from "../../config";
import type { PlatformAdapter } from "../../adapters/PlatformAdapter";

interface UICallbacks {
  onPickElement: (el: Element) => void;
  onChooseTower: (id: TowerId) => void;
  onStartWave: () => void;
  onSell: () => void;
  onUpgrade: () => void;
  onRewardGold: (amount: number) => void;
  onBack: () => void;
}

interface SelectedExtra {
  level: number;
  upgradeCost: number | null;
}

interface StatePayload {
  lives: number;
  gold: number;
  elements: Record<Element, number>;
  elementPoints: number;
  wave: number;
  totalWaves: number;
  intermission: boolean;
  affordableTowers: TowerId[];
  selectedTowerCell: Cell | null;
  towersByCell: Array<{ cell: Cell; def: TowerDef }>;
}

/** HUD overlay scene. Listens to GameScene events, never owns state. */
export class UIScene extends Phaser.Scene {
  private cb!: UICallbacks;
  private livesText!: Phaser.GameObjects.Text;
  private goldText!: Phaser.GameObjects.Text;
  private waveText!: Phaser.GameObjects.Text;
  private startBtn!: Phaser.GameObjects.Text;
  /** Rewarded-ad "bonus gold" button — shown between waves, once per wave. */
  private adBtn!: Phaser.GameObjects.Text;
  private adBusy = false;
  private adUsedForWave = -1;
  private lastWave = 0;
  private towerButtonsBox!: Phaser.GameObjects.Container;
  private towerButtons: Array<{ container: Phaser.GameObjects.Container; id: TowerId }> = [];
  private elementPicker?: Phaser.GameObjects.Container;
  private selectionInfo!: Phaser.GameObjects.Container;
  private backBtn!: Phaser.GameObjects.Text;
  /** Smoothly-animated gold readout state. */
  private displayedGold = 0;
  private goldTween?: Phaser.Tweens.Tween;
  private prevGold = -1;
  private prevLives = -1;
  private livesPulse?: Phaser.Tweens.Tween;
  /** Boss HP bar widgets (created on boss-spawn, destroyed on boss-dead). */
  private bossBar?: Phaser.GameObjects.Container;
  private bossBarFill?: Phaser.GameObjects.Rectangle;
  private bossBarMaxW = 0;

  constructor() {
    super({ key: "UIScene" });
  }

  init(cb: UICallbacks): void {
    this.cb = cb;
  }

  create(): void {
    const { width, height } = this.scale;
    const game = this.scene.get("GameScene");

    // Top bar background
    const topBg = this.add.rectangle(width / 2, 16, width, 32, 0x0b1020, 0.85);
    topBg.setStrokeStyle(0);

    this.livesText = this.addStat(12, 16, "♥", "#ff5a3a");
    this.goldText = this.addStat(width / 2, 16, "$", "#fff066");
    this.goldText.setOrigin(0.5, 0.5);
    this.waveText = this.addStat(width - 12, 16, "wave", "#aaa");
    this.waveText.setOrigin(1, 0.5);

    // Bottom HUD container — anchored directly under the map (no black gap)
    const MAP_BOTTOM = 8 + 12 * 48; // GridPath originY (8) + GRID_ROWS (12) * CELL_SIZE (48) = 584
    const hudY = MAP_BOTTOM + 4;
    const hudHeight = Math.max(140, height - hudY);
    const hudBg = this.add.rectangle(width / 2, hudY + hudHeight / 2, width, hudHeight, 0x0b1020, 0.97);
    hudBg.setStrokeStyle(0);
    const sep = this.add.rectangle(width / 2, hudY, width, 1, 0x2a3550);
    sep.setOrigin(0.5, 0);

    // Tower buttons (element strip removed to make room for more heroes)
    this.towerButtonsBox = this.add.container(0, 0);
    this.drawTowerButtons(width, hudY + 8);

    // Start wave button
    this.startBtn = this.add
      .text(width - 12, hudY - 12, "START ▶", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "14px",
        fontStyle: "bold",
        color: "#0b1020",
        backgroundColor: "#7be36b",
        padding: { x: 10, y: 6 },
      })
      .setOrigin(1, 1)
      .setInteractive({ useHandCursor: true });
    this.startBtn.on("pointerdown", () => this.cb.onStartWave());

    // Rewarded-ad bonus-gold button — centered along the bottom of the map,
    // between the menu/sound cluster (left) and START (right). Shown only
    // between waves, once per wave.
    this.adBtn = this.add
      .text(width / 2, hudY - 12, `📺 +${AD_REWARD_GOLD} GOLD`, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "13px",
        fontStyle: "bold",
        color: "#0b1020",
        backgroundColor: "#ffd84a",
        padding: { x: 10, y: 6 },
      })
      .setOrigin(0.5, 1)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });
    this.adBtn.on("pointerdown", () => this.onWatchAd());

    // Selected tower info + sell
    this.selectionInfo = this.add.container(0, 0);

    // Back button
    this.backBtn = this.add
      .text(12, hudY - 12, "← menu", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "11px",
        color: "#aaa",
        backgroundColor: "#1a2240",
        padding: { x: 6, y: 4 },
      })
      .setOrigin(0, 1)
      .setInteractive({ useHandCursor: true });
    this.backBtn.on("pointerdown", () => this.cb.onBack());

    // Mute toggle button — bottom-left, right of the "← menu" button (START owns the right side).
    const sound = this.registry.get("sound2") as { isMuted: () => boolean; toggleMuted: () => boolean } | undefined;
    const muteBtn = this.add
      .text(this.backBtn.x + this.backBtn.width + 8, hudY - 12, sound?.isMuted() ? "🔇 muted" : "🔊 sound", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "11px",
        color: "#aaa",
        backgroundColor: "#1a2240",
        padding: { x: 6, y: 4 },
      })
      .setOrigin(0, 1)
      .setInteractive({ useHandCursor: true });
    muteBtn.on("pointerdown", () => {
      const isMuted = sound?.toggleMuted();
      muteBtn.setText(isMuted ? "🔇 muted" : "🔊 sound");
    });

    // Listen for game state
    game.events.on("state", (s: StatePayload) => this.onState(s));
    game.events.on(
      "show-next-wave-prompt",
      (info: { cleared: number; next: number; total: number; perfStreak?: number; streakGold?: number }) =>
        this.showNextWavePrompt(info),
    );
    game.events.on("tower-selected", (def: TowerDef | null, extra?: SelectedExtra) =>
      this.onTowerSelected(def, extra),
    );
    game.events.on("boss-spawn", (info: { label: string; color: number; maxHp: number }) =>
      this.onBossSpawn(info),
    );
    game.events.on("boss-hp", (info: { hp: number; maxHp: number }) => this.onBossHp(info));
    game.events.on("boss-dead", () => this.onBossDead());
  }

  // ---------- boss bar ----------

  private onBossSpawn(info: { label: string; color: number; maxHp: number }): void {
    this.bossBar?.destroy();
    const { width } = this.scale;
    const barW = width - 40;
    const y = 44;
    this.bossBarMaxW = barW - 6;

    const hex = "#" + info.color.toString(16).padStart(6, "0");
    const frame = this.add.rectangle(width / 2, y, barW, 16, 0x000000, 0.7).setStrokeStyle(2, info.color, 1);
    const track = this.add.rectangle(width / 2, y, barW - 6, 10, 0x2a0a14, 1);
    const fill = this.add.rectangle(width / 2 - this.bossBarMaxW / 2, y, this.bossBarMaxW, 10, info.color, 1).setOrigin(0, 0.5);
    const name = this.add
      .text(width / 2, y - 16, `☠ ${info.label.toUpperCase()} ☠`, {
        fontFamily: "Georgia, 'Times New Roman', serif",
        fontSize: "14px",
        fontStyle: "bold",
        color: hex,
        stroke: "#000",
        strokeThickness: 4,
      })
      .setOrigin(0.5);
    name.setShadow(0, 2, "#000", 4, false, true);

    this.bossBarFill = fill;
    this.bossBar = this.add.container(0, 0, [frame, track, fill, name]).setDepth(450);

    // Dramatic name-banner sweep across the screen.
    this.showBossBanner(info.label, info.color);

    // Bar slides down into view.
    this.bossBar.y = -40;
    this.tweens.add({ targets: this.bossBar, y: 0, duration: 320, ease: "Back.easeOut" });
  }

  private showBossBanner(label: string, color: number): void {
    const { width, height } = this.scale;
    const hex = "#" + color.toString(16).padStart(6, "0");
    const stripe = this.add.rectangle(width / 2, height * 0.4, width, 56, 0x000000, 0.78).setDepth(600);
    const warn = this.add
      .text(width / 2, height * 0.4 - 12, "⚠ BOSS INCOMING ⚠", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "13px",
        fontStyle: "bold",
        color: "#ff5a3a",
      })
      .setOrigin(0.5)
      .setDepth(601);
    const title = this.add
      .text(width / 2, height * 0.4 + 10, label.toUpperCase(), {
        fontFamily: "Georgia, 'Times New Roman', serif",
        fontSize: "30px",
        fontStyle: "bold",
        color: hex,
        stroke: "#000",
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setDepth(601);
    title.setShadow(0, 3, "#000", 6, false, true);

    const items = [stripe, warn, title];
    for (const o of items) (o as Phaser.GameObjects.Components.Alpha).alpha = 0;
    // Slam in, hold, fade out.
    this.tweens.add({ targets: items, alpha: 1, duration: 200, ease: "Quad.easeOut" });
    this.tweens.add({
      targets: title,
      scale: { from: 1.6, to: 1 },
      duration: 300,
      ease: "Back.easeOut",
    });
    this.tweens.add({
      targets: items,
      alpha: 0,
      delay: 1400,
      duration: 400,
      ease: "Quad.easeIn",
      onComplete: () => items.forEach((o) => o.destroy()),
    });
  }

  private onBossHp(info: { hp: number; maxHp: number }): void {
    if (!this.bossBarFill) return;
    const ratio = Math.max(0, Math.min(1, info.hp / info.maxHp));
    this.bossBarFill.width = this.bossBarMaxW * ratio;
    // Color shifts toward red as the boss nears death.
    this.bossBarFill.fillColor = ratio > 0.5 ? 0xff4a4a : ratio > 0.25 ? 0xff8a3a : 0xffd84a;
  }

  private onBossDead(): void {
    if (!this.bossBar) return;
    const bar = this.bossBar;
    this.bossBar = undefined;
    this.bossBarFill = undefined;
    this.tweens.add({
      targets: bar,
      alpha: 0,
      scaleX: 1.1,
      duration: 400,
      ease: "Quad.easeIn",
      onComplete: () => bar.destroy(),
    });
  }

  /**
   * Procedural element glyph — element-shaped silhouettes instead of plain dots.
   * fire = upward flame teardrop, water = droplet, earth = layered mountain,
   * nature = leaf with stem.
   */
  private addElementGlyph(
    el: Element,
    cx: number,
    cy: number,
    size: number,
  ): Phaser.GameObjects.GameObject[] {
    const meta = ELEMENT_META[el];
    const out: Phaser.GameObjects.GameObject[] = [];
    const s = size; // base radius

    if (el === "fire") {
      // Outer flame body — teardrop (wider at base, peak at top)
      const outer = this.add
        .polygon(
          cx, cy,
          [
            0, -s * 1.05,        // peak
            s * 0.4, -s * 0.4,
            s * 0.7, s * 0.1,
            s * 0.5, s * 0.55,
            0, s * 0.7,
            -s * 0.5, s * 0.55,
            -s * 0.7, s * 0.1,
            -s * 0.4, -s * 0.4,
          ],
          0xff3a1f,
        )
        .setStrokeStyle(1, 0x000, 1);
      // Inner flame
      const inner = this.add.polygon(
        cx, cy + s * 0.1,
        [0, -s * 0.7, s * 0.3, -s * 0.1, s * 0.35, s * 0.3, 0, s * 0.45, -s * 0.35, s * 0.3, -s * 0.3, -s * 0.1],
        0xff8a3a,
      );
      // Yellow core
      const core = this.add.polygon(
        cx, cy + s * 0.25,
        [0, -s * 0.4, s * 0.18, 0, 0, s * 0.25, -s * 0.18, 0],
        0xfff066,
      );
      out.push(outer, inner, core);
    } else if (el === "water") {
      // Droplet — circle bottom, tapering point top
      const body = this.add
        .polygon(
          cx, cy,
          [
            0, -s * 1.05,
            s * 0.42, -s * 0.4,
            s * 0.7, s * 0.15,
            s * 0.55, s * 0.55,
            0, s * 0.7,
            -s * 0.55, s * 0.55,
            -s * 0.7, s * 0.15,
            -s * 0.42, -s * 0.4,
          ],
          0x4cc9f0,
        )
        .setStrokeStyle(1, 0x000, 1);
      // White highlight gleam
      const gleam = this.add.ellipse(
        cx - s * 0.25,
        cy - s * 0.05,
        s * 0.3,
        s * 0.55,
        0xffffff,
        0.7,
      );
      out.push(body, gleam);
    } else if (el === "earth") {
      // Layered mountain — back peak (darker) + front peak (lighter) + base ground
      const base = this.add.rectangle(cx, cy + s * 0.55, s * 1.7, s * 0.3, 0x4a3018);
      const back = this.add
        .triangle(cx, cy, 0, -s * 0.95, -s * 0.7, s * 0.45, s * 0.6, s * 0.45, 0x6a4a28)
        .setStrokeStyle(1, 0x000, 1);
      const front = this.add
        .triangle(cx + s * 0.2, cy + s * 0.15, 0, -s * 0.7, -s * 0.65, s * 0.4, s * 0.55, s * 0.4, meta.color)
        .setStrokeStyle(1, 0x000, 1);
      // Snow cap on back peak
      const cap = this.add.triangle(
        cx, cy - s * 0.55,
        0, -s * 0.4, -s * 0.22, 0, s * 0.18, 0,
        0xffffff,
      );
      out.push(base, back, front, cap);
    } else if (el === "nature") {
      // Leaf — almond shape with central vein, slight tilt + stem
      const leaf = this.add
        .polygon(
          cx, cy,
          [
            0, -s * 1.0,         // tip
            s * 0.55, -s * 0.35,
            s * 0.7, s * 0.15,
            s * 0.45, s * 0.55,
            0, s * 0.75,
            -s * 0.45, s * 0.55,
            -s * 0.7, s * 0.15,
            -s * 0.55, -s * 0.35,
          ],
          meta.color,
        )
        .setStrokeStyle(1, 0x000, 1);
      // Central vein
      const vein = this.add
        .line(0, 0, cx, cy - s * 0.95, cx, cy + s * 0.7, 0x2a6a2a)
        .setLineWidth(1)
        .setOrigin(0, 0);
      // Side veins
      const veinL = this.add
        .line(0, 0, cx, cy - s * 0.2, cx - s * 0.5, cy + s * 0.3, 0x2a6a2a)
        .setLineWidth(0.6)
        .setOrigin(0, 0);
      const veinR = this.add
        .line(0, 0, cx, cy - s * 0.2, cx + s * 0.5, cy + s * 0.3, 0x2a6a2a)
        .setLineWidth(0.6)
        .setOrigin(0, 0);
      // Stem
      const stem = this.add.rectangle(cx, cy + s * 0.95, s * 0.12, s * 0.4, 0x4a3018);
      out.push(stem, leaf, vein, veinL, veinR);
    }
    return out;
  }

  private addStat(x: number, y: number, prefix: string, color: string): Phaser.GameObjects.Text {
    return this.add
      .text(x, y, `${prefix} 0`, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "14px",
        fontStyle: "bold",
        color,
      })
      .setOrigin(0, 0.5);
  }

  // ---------- tower buttons ----------

  private drawTowerButtons(width: number, y: number): void {
    const padding = 6;
    const btnW = 70;
    const btnH = 78;
    const cols = Math.floor((width - padding * 2) / (btnW + padding));
    let row = 0;
    let col = 0;
    TOWER_LIST.forEach((t) => {
      const cx = padding + col * (btnW + padding) + btnW / 2;
      const cy = y + row * (btnH + padding) + btnH / 2;
      // Dark portrait bg with luxury gold double-frame.
      const bg = this.add.rectangle(cx, cy, btnW, btnH, 0x0a0e1f)
        .setStrokeStyle(2, 0x2a3550, 1)
        .setInteractive({ useHandCursor: true });
      // Outer gold frame
      const frameOuter = this.add.rectangle(cx, cy, btnW - 4, btnH - 4, 0x000000, 0)
        .setStrokeStyle(1.4, 0xffd84a, 0.95);
      // Inner thin gold frame for double-line luxury feel
      const frameInner = this.add.rectangle(cx, cy, btnW - 9, btnH - 9, 0x000000, 0)
        .setStrokeStyle(0.6, 0x6a3a08, 1);
      // Gold corner accents (small triangles in each corner)
      const cornerSize = 5;
      const cTL = this.add.triangle(cx - btnW / 2 + 4, cy - btnH / 2 + 4,
        0, 0, cornerSize, 0, 0, cornerSize, 0xfff080).setAlpha(0.95);
      const cTR = this.add.triangle(cx + btnW / 2 - 4, cy - btnH / 2 + 4,
        0, 0, -cornerSize, 0, 0, cornerSize, 0xfff080).setAlpha(0.95);
      const cBL = this.add.triangle(cx - btnW / 2 + 4, cy + btnH / 2 - 4,
        0, 0, cornerSize, 0, 0, -cornerSize, 0xfff080).setAlpha(0.95);
      const cBR = this.add.triangle(cx + btnW / 2 - 4, cy + btnH / 2 - 4,
        0, 0, -cornerSize, 0, 0, -cornerSize, 0xfff080).setAlpha(0.95);
      // Show the actual hero portrait, BIG, behind the gold frame.
      const glyphParts: Phaser.GameObjects.GameObject[] = [];
      const hero = makeCaster(this, t.id, cx, cy - 6, 0);
      hero.setScale(1.25);
      glyphParts.push(hero, frameOuter, frameInner, cTL, cTR, cBL, cBR);
      // Name plate banner across the bottom (gold bar with dark text)
      const nameBar = this.add.rectangle(cx, cy + btnH / 2 - 12, btnW - 8, 12, 0x1a0f04, 0.85)
        .setStrokeStyle(0.6, 0xffd84a, 1);
      const lbl = this.add
        .text(cx, cy + btnH / 2 - 14, t.label, {
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: "10px",
          fontStyle: "bold",
          color: "#ffd84a",
        })
        .setOrigin(0.5);
      const cost = this.add
        .text(cx, cy + btnH / 2 - 4, `$${t.cost}`, {
          fontFamily: "system-ui, sans-serif",
          fontSize: "9px",
          color: "#fff066",
        })
        .setOrigin(0.5);
      // Lock overlay sits ABOVE the button visually but never blocks input —
      // the button bg's interactive zone is the sole click receiver.
      const lock = this.add.rectangle(cx, cy, btnW, btnH, 0x000000, 0.6);
      lock.setData("name", "lock");

      bg.on("pointerover", () => bg.setStrokeStyle(2, t.color, 1));
      bg.on("pointerout", () => bg.setStrokeStyle(2, 0x2a3550, 1));
      bg.on("pointerdown", () => this.cb.onChooseTower(t.id));

      // Keep references for state-driven appearance updates.
      const container = this.add.container(0, 0, [bg, ...glyphParts, nameBar, lbl, cost, lock]);
      this.towerButtons.push({ container, id: t.id });
      this.towerButtonsBox.add(container);

      col++;
      if (col >= cols) {
        col = 0;
        row++;
      }
    });
  }

  // ---------- next wave prompt ----------

  /**
   * Shown after a wave is cleared. Big "WAVE N CLEARED" headline + a centered
   * NEXT WAVE button. Replaces the old "pick an element" modal.
   */
  private showNextWavePrompt(info: { cleared: number; next: number; total: number; perfStreak?: number; streakGold?: number }): void {
    if (this.elementPicker) this.elementPicker.destroy();
    const { width, height } = this.scale;

    const created: Phaser.GameObjects.GameObject[] = [];

    // No dim — keep the play area visible. Just a centered card.
    const panel = this.add
      .rectangle(width / 2, height / 2, width - 60, 180, 0x141a35, 0.95)
      .setStrokeStyle(3, 0x7be36b, 0.9)
      .setDepth(500);
    created.push(panel);

    const cleared = this.add
      .text(width / 2, height / 2 - 50, `WAVE ${info.cleared} CLEARED`, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "20px",
        fontStyle: "bold",
        color: "#7be36b",
      })
      .setOrigin(0.5)
      .setDepth(501);
    created.push(cleared);

    const sub = this.add
      .text(width / 2, height / 2 - 24, `${info.cleared} / ${info.total}`, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "13px",
        color: "#aaa",
      })
      .setOrigin(0.5)
      .setDepth(501);
    created.push(sub);

    // ── Perfect-wave streak badge ─────────────────────────────────────
    if ((info.perfStreak ?? 0) >= 1) {
      const streak = info.perfStreak!;
      const streakLabel = streak === 1
        ? "PERFECT WAVE"
        : `PERFECT STREAK  x${streak}`;
      // Glow background pill
      const pillW = streak === 1 ? 148 : 174;
      const streakPill = this.add
        .rectangle(width / 2, height / 2 - 6, pillW, 20, 0xff5500, 0.88)
        .setStrokeStyle(1.5, 0xffcc44, 1)
        .setDepth(502);
      created.push(streakPill);

      const streakTxt = this.add
        .text(width / 2, height / 2 - 6, streakLabel, {
          fontFamily: "system-ui, sans-serif",
          fontSize: streak >= 3 ? "13px" : "12px",
          fontStyle: "bold",
          color: streak >= 3 ? "#ffee44" : "#ffffff",
        })
        .setOrigin(0.5)
        .setDepth(503);
      created.push(streakTxt);

      // Pulse animation — more intense for longer streaks.
      const pulseScale = streak >= 5 ? 1.12 : streak >= 3 ? 1.09 : 1.05;
      const pulseDur   = streak >= 3 ? 350 : 480;
      this.tweens.add({
        targets: [streakPill, streakTxt],
        scale: { from: 1, to: pulseScale },
        yoyo: true,
        repeat: -1,
        duration: pulseDur,
        ease: "Sine.easeInOut",
      });

      // Streak gold bonus line (only shows at streak ≥3)
      if ((info.streakGold ?? 0) > 0) {
        const bonusTxt = this.add
          .text(width / 2, height / 2 + 10, `+${info.streakGold} 🌿 streak bonus`, {
            fontFamily: "system-ui, sans-serif",
            fontSize: "11px",
            fontStyle: "bold",
            color: "#c8ff8a",
          })
          .setOrigin(0.5)
          .setDepth(502);
        created.push(bonusTxt);
      }
    }

    if (info.next > info.total) {
      // No next wave — game is essentially won; let GameOver flow handle it.
      const done = this.add
        .text(width / 2, height / 2 + 20, "All waves complete!", {
          fontFamily: "system-ui, sans-serif",
          fontSize: "14px",
          color: "#fff066",
        })
        .setOrigin(0.5)
        .setDepth(501);
      created.push(done);
    } else {
      const nextBtn = this.add
        .text(width / 2, height / 2 + 28, `WAVE ${info.next}  ▶`, {
          fontFamily: "system-ui, sans-serif",
          fontSize: "22px",
          fontStyle: "bold",
          color: "#0b1020",
          backgroundColor: "#7be36b",
          padding: { x: 22, y: 12 },
        })
        .setOrigin(0.5)
        .setDepth(502)
        .setInteractive({ useHandCursor: true });
      nextBtn.on("pointerover", () => nextBtn.setBackgroundColor("#9bf08a"));
      nextBtn.on("pointerout", () => nextBtn.setBackgroundColor("#7be36b"));
      nextBtn.on("pointerdown", () => {
        for (const obj of created) if (obj.active) obj.destroy();
        this.elementPicker = undefined;
        this.cb.onStartWave();
      });
      created.push(nextBtn);

      // Subtle pulse so it's eye-catching.
      this.tweens.add({
        targets: nextBtn,
        scale: { from: 1, to: 1.06 },
        yoyo: true,
        repeat: -1,
        duration: 600,
        ease: "Sine.easeInOut",
      });
    }

    // Slide-in flourish.
    panel.setScale(0.85);
    panel.alpha = 0;
    this.tweens.add({
      targets: panel,
      scale: 1,
      alpha: 0.95,
      duration: 220,
      ease: "Back.easeOut",
    });

    const tracker = this.add.container(0, 0);
    const origDestroy = tracker.destroy.bind(tracker);
    tracker.destroy = (fromScene?: boolean) => {
      for (const obj of created) {
        if (obj.active) obj.destroy();
      }
      origDestroy(fromScene);
    };
    this.elementPicker = tracker;
  }

  // ---------- selected tower info ----------

  private onTowerSelected(def: TowerDef | null, extra?: SelectedExtra): void {
    this.selectionInfo.removeAll(true);
    if (!def) return;
    const { width, height } = this.scale;
    const y = height - 130 - 56;

    const lvlRoman = ["I", "II", "III"][(extra?.level ?? 1) - 1] ?? "I";
    const bg = this.add.rectangle(width / 2, y, width - 24, 36, 0x141a35).setStrokeStyle(1, def.color, 0.8);
    const txt = this.add
      .text(20, y, `${def.label} ${lvlRoman}  •  ${def.description}`, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "11px",
        color: "#fff",
      })
      .setOrigin(0, 0.5);
    this.selectionInfo.add([bg, txt]);

    const sell = this.add
      .text(width - 20, y, "SELL", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "11px",
        fontStyle: "bold",
        color: "#0b1020",
        backgroundColor: "#ff9b3a",
        padding: { x: 6, y: 4 },
      })
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });
    sell.on("pointerdown", () => this.cb.onSell());
    this.selectionInfo.add(sell);

    if (extra && extra.upgradeCost != null) {
      const upgrade = this.add
        .text(width - 70, y, `▲ $${extra.upgradeCost}`, {
          fontFamily: "system-ui, sans-serif",
          fontSize: "11px",
          fontStyle: "bold",
          color: "#0b1020",
          backgroundColor: "#7be36b",
          padding: { x: 6, y: 4 },
        })
        .setOrigin(1, 0.5)
        .setInteractive({ useHandCursor: true });
      upgrade.on("pointerdown", () => this.cb.onUpgrade());
      this.selectionInfo.add(upgrade);
    } else if (extra && extra.upgradeCost == null) {
      const maxed = this.add
        .text(width - 70, y, "MAX", {
          fontFamily: "system-ui, sans-serif",
          fontSize: "10px",
          fontStyle: "bold",
          color: "#fff066",
          backgroundColor: "#1a2240",
          padding: { x: 6, y: 4 },
        })
        .setOrigin(1, 0.5);
      this.selectionInfo.add(maxed);
    }
  }

  // ---------- state ----------

  /** Count gold up/down smoothly, and float a "+N" when it increases. */
  private updateGold(gold: number): void {
    if (this.prevGold < 0) {
      // First state — snap, no animation.
      this.displayedGold = gold;
      this.prevGold = gold;
      this.goldText.setText(`$ ${gold}`);
      return;
    }
    const delta = gold - this.prevGold;
    if (delta > 0) this.floatGoldGain(delta);
    if (delta !== 0) {
      // Coin pop on the readout.
      this.tweens.add({
        targets: this.goldText,
        scale: { from: 1.18, to: 1 },
        duration: 180,
        ease: "Back.easeOut",
      });
      this.goldTween?.remove();
      const from = this.displayedGold;
      const ref = { v: from };
      this.goldTween = this.tweens.add({
        targets: ref,
        v: gold,
        duration: 320,
        ease: "Cubic.easeOut",
        onUpdate: () => {
          this.displayedGold = Math.round(ref.v);
          this.goldText.setText(`$ ${this.displayedGold}`);
        },
        onComplete: () => {
          this.displayedGold = gold;
          this.goldText.setText(`$ ${gold}`);
        },
      });
    }
    this.prevGold = gold;
  }

  private floatGoldGain(amount: number): void {
    const t = this.add
      .text(this.goldText.x + 26, this.goldText.y, `+${amount}`, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "13px",
        fontStyle: "bold",
        color: "#7be36b",
        stroke: "#000",
        strokeThickness: 3,
      })
      .setOrigin(0, 0.5)
      .setDepth(300);
    this.tweens.add({
      targets: t,
      y: t.y - 16,
      alpha: { from: 1, to: 0 },
      duration: 700,
      ease: "Cubic.easeOut",
      onComplete: () => t.destroy(),
    });
  }

  /** Update lives text and pulse it red when dangerously low. */
  private updateLives(lives: number): void {
    this.livesText.setText(`♥ ${lives}`);
    const low = lives > 0 && lives <= 5;
    if (low && !this.livesPulse) {
      this.livesText.setColor("#ff3a2a");
      this.livesPulse = this.tweens.add({
        targets: this.livesText,
        scale: { from: 1, to: 1.22 },
        yoyo: true,
        repeat: -1,
        duration: 520,
        ease: "Sine.easeInOut",
      });
    } else if (!low && this.livesPulse) {
      this.livesPulse.remove();
      this.livesPulse = undefined;
      this.livesText.setScale(1);
      this.livesText.setColor("#ff5a3a");
    }
    // Flash on actual loss.
    if (this.prevLives >= 0 && lives < this.prevLives) {
      this.tweens.add({
        targets: this.livesText,
        scale: { from: 1.5, to: low ? 1.22 : 1 },
        duration: 220,
        ease: "Back.easeOut",
      });
    }
    this.prevLives = lives;
  }

  private onState(s: StatePayload): void {
    this.updateGold(s.gold);
    this.updateLives(s.lives);
    this.waveText.setText(`wave ${Math.max(s.wave, 0)}/${s.totalWaves}`);

    // Tower button affordability + lock state
    const affordable = new Set(s.affordableTowers);
    for (const b of this.towerButtons) {
      const def = TOWERS[b.id];
      const unlocked = affordable.has(b.id);
      const canAfford = unlocked && s.gold >= def.cost;
      const lock = b.container.list.find((o) => (o as Phaser.GameObjects.GameObject).getData?.("name") === "lock") as Phaser.GameObjects.Rectangle | undefined;
      if (lock) lock.setVisible(!unlocked);
      b.container.setAlpha(unlocked ? (canAfford ? 1 : 0.45) : 0.5);
    }

    // Start button visibility
    if (s.intermission && s.wave < s.totalWaves) {
      this.startBtn.setVisible(true);
      const isFirst = s.wave === 0;
      this.startBtn.setText(isFirst ? "START ▶" : `WAVE ${s.wave + 1} ▶`);
    } else {
      this.startBtn.setVisible(false);
    }

    // Rewarded-ad button: only between waves, once per wave, and not while
    // an ad request is in flight.
    this.lastWave = s.wave;
    const canShowAd =
      s.intermission &&
      s.wave < s.totalWaves &&
      this.adUsedForWave !== s.wave &&
      !this.adBusy;
    this.adBtn.setVisible(canShowAd);
  }

  private async onWatchAd(): Promise<void> {
    if (this.adBusy || this.adUsedForWave === this.lastWave) return;
    this.adBusy = true;
    this.adBtn.setText("loading…").disableInteractive();

    const adapter = this.registry.get("adapter") as PlatformAdapter | undefined;
    let rewarded = false;
    try {
      const res = await adapter?.showRewardedAd();
      rewarded = res?.rewarded ?? false;
    } catch {
      rewarded = false;
    }

    if (rewarded) {
      this.adUsedForWave = this.lastWave;
      this.cb.onRewardGold(AD_REWARD_GOLD);
    }

    this.adBusy = false;
    this.adBtn
      .setText(`📺 +${AD_REWARD_GOLD} GOLD`)
      .setInteractive({ useHandCursor: true })
      .setVisible(!rewarded && this.adUsedForWave !== this.lastWave);
  }
}
